import { ChangeDetectionStrategy, Component, Injector, OnDestroy, OnInit, computed, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Subscription, combineLatest, debounceTime, distinctUntilChanged, startWith, tap } from 'rxjs';
import { ctrlCassetteCode, ctrlContainerNumber, ctrlContainerPrefix, ctrlContainerSearch, ctrlSequenceNumber, sectionContainerNumber } from '../../_config/ctrls/ctrls-constants';
import { sharedContainerSearchActions, sharedContainerSearchReducer } from 'src/app/store/shared-container-search';
import { textSearch, textUnitId } from '../../_config/i18n/i18n-interface';

import { SharedContainerSearchI } from 'src/app/api/shared-container-search/shared-container-search.result.int';
import { SharedFieldTypeEnum } from 'src/app/shared/models/shared/shared-field-type.enum';
import { SharedFormViewComponent } from 'src/app/pages/_templates/shared-form-view/shared-form-view.component';
import { SharedSearchChildContextI } from './shared-search-child-context.interface';
import { SharedSectionI } from 'src/app/shared/models/shared/shared-section.interface';
import { SharedValuePairInterface } from '../../models/shared/shared-value-pair.interface';
import { Store } from '@ngrx/store';
import { sharedValidator } from '../../_config/validators/validators';
import { spValRequired } from '../../validators';
import { valuePairToValue } from '@softpak/components/spx-helpers';

@Component({
  selector: 'shared-search-miscellaneous',
  templateUrl: './shared-search-miscellaneous.component.html',
  imports: [SharedFormViewComponent],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharedSearchMiscellaneousComponent implements OnInit, OnDestroy {
  private readonly injector = inject(Injector);
  private readonly appStore = inject(Store);
  private readonly sharedContainerSearchState = this.appStore.selectSignal(
    sharedContainerSearchReducer.default.selectSharedContainerSearchState
  );

  formGroup = input.required<FormGroup>();
  context = input.required<SharedSearchChildContextI>();
  buttonClick = output<any>();
  containerClearRequest = output<void>();
  private readonly containerSuggestions = signal<SharedValuePairInterface[]>([]);
  private readonly formView = viewChild<SharedFormViewComponent>('formView');
  private readonly ctrlContainerNumber = computed(
    () => this.formGroup().get(ctrlContainerNumber) as FormControl | null
  );
  private readonly ctrlContainerPrefix = computed(
    () => this.formGroup().get(ctrlContainerPrefix) as FormControl | null
  );
  private readonly ctrlSeqNr = computed(
    () => this.formGroup().get(ctrlSequenceNumber) as FormControl | null
  );
  private readonly ctrlCassetteCode = computed(
    () => this.formGroup().get(ctrlCassetteCode) as FormControl | null
  );

  readonly sections: SharedSectionI[] = [
    {
      key: sectionContainerNumber,
      index: 2,
      show: () => true,
      showTitle: () => false,
      fields: [
        {
          key: ctrlContainerSearch,
          type: SharedFieldTypeEnum.buttons,
          entity: undefined,
          defaultValue: null,
          validators: [],
          readonly: () => false,
          suggestions: () => false,
          afterValueChange: undefined,
          foreignKeys: undefined,
          show: () => this.context().showSearch,
          buttons: [
            {
              key: 'search',
              text: textSearch,
            },
          ]
        },
        {
          label: () => textUnitId,
          key: ctrlContainerNumber,
          type: SharedFieldTypeEnum.overlay,
          entity: undefined,
          defaultValue: null,
          validators: [...sharedValidator.containerDigits],
          readonly: () => false,
          suggestions: () => true,
          suggestionsFixed: () => this.containerSuggestions(),
          afterValueChange: undefined,
          foreignKeys: undefined,
          show: () => true,
          capitalize: true,
          autofocus: () => true,
        },
      ],
    },
  ];

  private readonly subscriptions: {
    miscNumberInput?: Subscription;
    miscCombination?: Subscription;
  } = {};

  ngOnInit(): void {
    this.configureMiscellaneousMode();
    this.listenToMiscNumberInput();
    this.listenToMiscCombination();
    this.listenToMiscSearchResult();
  }

  ngOnDestroy(): void {
    this.subscriptions.miscNumberInput?.unsubscribe();
    this.subscriptions.miscCombination?.unsubscribe();
  }

  onButtonClick(key: string): void {
    this.buttonClick.emit(key);
  }

  triggerChangeDetection(): void {
    this.formView()?.triggerChangeDetection();
  }

  applyFocus(fieldName: string): void {
    this.formView()?.applyFocus(fieldName);
  }

  applyAutofocus(): void {
    this.formView()?.applyAutofocus();
  }

  private listenToMiscNumberInput(): void {
    this.subscriptions.miscNumberInput?.unsubscribe();
    const containerNumberControl = this.ctrlContainerNumber();
    if (!containerNumberControl) {
      return;
    }

    this.subscriptions.miscNumberInput = containerNumberControl.valueChanges.pipe(
      tap((valuePair) => {
        const prefixControl = this.ctrlContainerPrefix();
        const seqNrControl = this.ctrlSeqNr();
        if (!prefixControl || !seqNrControl) {
          return;
        }

        const rawValue = valuePairToValue(valuePair);
        const value = typeof rawValue === 'string' ? decodeURI(rawValue) : '';
        if (value && value.includes('SQNR')) {
          seqNrControl.setValue({ value: value.split('|')[0].replace('SQNR', '') });
          containerNumberControl.setValue({ value: value.split('|')[1].trim().substring(4, 11) }, { emitEvent: false });
          prefixControl.setValue({ value: value.split('|')[1].trim().substring(0, 4) }, { emitEvent: false });
        }
      }),
      debounceTime(1000),
      distinctUntilChanged((a, b) => valuePairToValue(a) === valuePairToValue(b))
    ).subscribe((valuePair) => {
      const rawValue = valuePairToValue(valuePair);
      if (rawValue && !rawValue.includes('SQNR') && rawValue.length >= 3) {
        this.appStore.dispatch(sharedContainerSearchActions.load({
          pageType: this.context().pageType,
          query: {
            ...this.context().searchParameters,
            queryString: rawValue,
            recordType: this.context().recordType,
            inQuayjobId: this.context().inQuayjobId,
            isMiscellaneous: true,
          }
        }));
      }
    });
  }

  private configureMiscellaneousMode(): void {
    const containerNumberControl = this.ctrlContainerNumber();
    const containerPrefixControl = this.ctrlContainerPrefix();
    const cassetteCodeControl = this.ctrlCassetteCode();
    if (!containerNumberControl || !containerPrefixControl || !cassetteCodeControl) {
      return;
    }

    containerNumberControl.setValidators([spValRequired()]);
    containerPrefixControl.setValidators([spValRequired()]);
    cassetteCodeControl.clearValidators();
    if (!valuePairToValue(containerPrefixControl.value)) {
      containerPrefixControl.setValue({ value: 'M' }, { emitEvent: false });
    }

    containerNumberControl.updateValueAndValidity({ emitEvent: false });
    containerPrefixControl.updateValueAndValidity({ emitEvent: false });
    cassetteCodeControl.updateValueAndValidity({ emitEvent: false });

    setTimeout(() => {
      this.applyFocus(ctrlContainerNumber);
    }, 0);
  }

  private listenToMiscCombination(): void {
    this.subscriptions.miscCombination?.unsubscribe();
    const containerNumberControl = this.ctrlContainerNumber();
    const containerPrefixControl = this.ctrlContainerPrefix();
    if (!containerNumberControl || !containerPrefixControl) {
      return;
    }

    this.subscriptions.miscCombination = combineLatest([
      containerNumberControl.valueChanges.pipe(startWith(containerNumberControl.value)),
      containerPrefixControl.valueChanges.pipe(startWith(containerPrefixControl.value)),
    ]).subscribe(() => {
      const containerNumber = valuePairToValue(containerNumberControl.value);
      const containerPrefix = valuePairToValue(containerPrefixControl.value);
      const containerNumberValue = typeof containerNumber === 'string' ? containerNumber : '';
      const containerPrefixValue = typeof containerPrefix === 'string' ? containerPrefix : '';

      if (
        !(containerNumberValue && !containerNumberValue.includes('SQNR')) &&
        (this.formGroup().invalid ||
          !containerNumberValue ||
          !containerPrefixValue ||
          containerNumberValue.length < 7 ||
          containerPrefixValue.length < 1)
      ) {
        this.containerClearRequest.emit();
      }
    });
  }

  private listenToMiscSearchResult(): void {
    effect(() => {
      const containerNumberControl = this.ctrlContainerNumber();
      const containerPrefixControl = this.ctrlContainerPrefix();
      const seqNrControl = this.ctrlSeqNr();
      if (!containerNumberControl || !containerPrefixControl || !seqNrControl) {
        return;
      }

      const state = this.sharedContainerSearchState();
      const pageState = state[this.context().pageType];
      const currentContainerNumber = valuePairToValue(containerNumberControl.value);
      if (currentContainerNumber?.length >= 1 && pageState?.loaded) {
        const result = pageState.data as SharedContainerSearchI;
        if (result?.containers?.length >= 2) {
          this.containerSuggestions.set(result.containers.map((container) => ({
            description: `${valuePairToValue(container.containerPrefix)}${valuePairToValue(container.containerNumber)}`,
            value: `SQNR${valuePairToValue(container.seqNr)}|${valuePairToValue(container.containerPrefix)}${valuePairToValue(container.containerNumber)}`,
          })));
        } else if (result?.containers?.length === 1) {
          this.containerSuggestions.set([]);
          containerNumberControl.setValue({ value: valuePairToValue(result.containers[0].containerNumber) }, { emitEvent: false });
          containerPrefixControl.setValue({ value: valuePairToValue(result.containers[0].containerPrefix) }, { emitEvent: true });
          seqNrControl.setValue({ value: valuePairToValue(result.containers[0].seqNr) });
        } else {
          this.containerSuggestions.set([]);
        }
      } else {
        this.containerSuggestions.set([]);
      }
    }, { injector: this.injector });
  }
}
